import { useState } from 'react'
import { Link } from 'react-router-dom'
import { GENDERS } from '../api/types'
import { CheckIcon } from '../components/Icons'
import { COUNTRIES } from '../countries'
import type { Gender } from '../api/types'
import { useAuth } from '../auth'

/**
 * The account's own details. These live on the account rather than in the phone's settings so
 * they follow the person rather than the device, and so the website can show what the calorie
 * estimate was scored against.
 *
 * Metric throughout, matching what the API stores. The Android settings screen converts for
 * whoever has it set to imperial; the figures underneath are the same either way.
 */
export function ProfilePage() {
  const { user, saveProfile } = useAuth()

  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [height, setHeight] = useState(user?.heightCm?.toString() ?? '')
  const [weight, setWeight] = useState(user?.weightKg?.toString() ?? '')
  const [dateOfBirth, setDateOfBirth] = useState(user?.dateOfBirth ?? '')
  const [gender, setGender] = useState<Gender | ''>(user?.gender ?? '')
  const [country, setCountry] = useState(user?.country ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setSaved(false)
    setBusy(true)
    try {
      // An emptied box means "forget this", which the API expresses as null.
      await saveProfile({
        displayName: displayName.trim(),
        heightCm: height.trim() === '' ? null : Number(height),
        weightKg: weight.trim() === '' ? null : Number(weight),
        dateOfBirth: dateOfBirth.trim() === '' ? null : dateOfBirth,
        gender: gender === '' ? null : gender,
        country: country === '' ? null : country,
      })
      setSaved(true)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  // Today, so the date picker cannot offer a birthday that has not happened.
  const today = new Date().toISOString().slice(0, 10)

  return (
    <div className="page page-narrow">
      <div className="page-head">
        <div>
          <Link to="/" className="muted">
            ← All activities
          </Link>
          <h1 style={{ marginTop: 6 }}>Your profile</h1>
          <div className="muted">{user?.email}</div>
        </div>
      </div>

      <form className="card form-card" onSubmit={submit}>
        <label htmlFor="displayName">Name</label>
        <input
          id="displayName"
          type="text"
          autoComplete="name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />

        {/* Paired: both are short metric numbers, and a full-width box for three digits looks
            wrong beside its label. */}
        <div className="field-pair">
          <div>
            <label htmlFor="heightCm">Height (cm)</label>
            <input
              id="heightCm"
              type="number"
              inputMode="decimal"
              min={50}
              max={260}
              step="0.5"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="weightKg">Weight (kg)</label>
            <input
              id="weightKg"
              type="number"
              inputMode="decimal"
              min={20}
              max={400}
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            />
          </div>
        </div>

        <div className="field-pair">
          <div>
            <label htmlFor="dateOfBirth">Date of birth</label>
            <input
              id="dateOfBirth"
              type="date"
              max={today}
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value as Gender | '')}
            >
              <option value="">Not set</option>
              {GENDERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="muted hint">
          {/* The date is stored rather than an age, which would be right only until the next
              birthday. The server works the age out on every read. */}
          {user?.age !== undefined && user?.age !== null
            ? `That makes you ${user.age}.`
            : 'Used to work out your age, which some calorie formulas need.'}
        </p>

        <label htmlFor="country">Country</label>
        <select
          id="country"
          autoComplete="country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        >
          <option value="">Not set</option>
          {COUNTRIES.map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>

        <p className="muted form-note">
          Weight sets the calorie estimate for activities recorded from now on; those already
          recorded keep the figure they were scored against. Every field here is optional — leave
          one empty to forget it.
        </p>

        {error && <div className="error">{error}</div>}
        {saved && !error && <div className="notice">Saved. Your phone will pick this up too.</div>}

        <button className="primary" type="submit" disabled={busy}>
          <CheckIcon />
          {busy ? 'Saving…' : 'Save profile'}
        </button>
      </form>

      <div className="card form-card" style={{ marginTop: 18 }}>
        <strong>Delete your account</strong>
        <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
          Removes your account and every activity on it, permanently. Download anything you want
          to keep first.
        </p>
        <Link className="link" to="/delete-account">
          Delete my account
        </Link>
      </div>
    </div>
  )
}
